---
title: "Python built-ins in Google Sheets"
subtitle: "for when you want to filter with a lambda"
date: 2025-05-08 20:45
---

import { Linker } from './Linker';

<Linker />

Some notes:

- I use "vector" for a one-dimensional array, and "array" for the general case.

- Custom functions don't have optional arguments in the Python sense; you can explicitly pass a blank argument with an error value like `1/0{:gsheets}`.

- I'm gonna implicitly convert between Google Sheets's one-indexing to Python's zero-indexing. Python functions take zero-indices, and Google Sheets functions take one-indices, unless otherwise stated. This means I'll write `list[i]{:py}` as the Python equivalent of `INDEX(list, i){:gsheets}`, even though the Google Sheets `i` is one larger than the Python `i`.

- Some of these depend on length-zero arrays, which don't exist in Google Sheets right now. I use `{}{:gsheets}` for this, even though it'll produce an error.

- If I made a mistake, please tell me. If you have better solutions than mine, I'd love to hear them. Get in the comments!

## Built-in functions

**`abs(){:py}`.** Use `ABS{:gsheets}`.

**`all(){:py}`.** The native equivalent doesn't short-circuit. To get short-circuiting behavior, use:

```gsheets
ALL(vector)
= REDUCE(TRUE, vector, LAMBDA(x, y, IF(x, y, FALSE)))
```

If you need to coerce to booleans, see [`bool(){:py}`.](#bool)

**`any(){:py}`.** The native equivalent doesn't short-circuit. To get short-circuiting behavior, use:

```gsheets
ANY(vector)
= REDUCE(FALSE, vector, LAMBDA(x, y, IF(x, TRUE, y)))
```

If you need to coerce to booleans, see [`bool(){:py}`.](#bool)

**`bin(){:py}`.** Use `DEC2BIN{:gsheets}`.

<a name="bool"></a>

**`bool(){:py}`.** You can read the native coercion logic on [sheets.wiki.](https://sheets.wiki/docs/Type-coercion) To get Python-style coercion, use:

```gsheets
BOOL(val)
= SWITCH(TYPE(val),
  1, val <> 0,
  2, val <> "",
  4, val,
  64, AND(ROWS(val) > 0, COLUMNS(val) > 0),
  TRUE
)
```

**`chr(){:py}`.** Use `CHAR{:gsheets}`.

**`complex(){:py}`.** Use `COMPLEX{:gsheets}`.

**`dict(){:py}`.** See [Dictionaries](#dictionaries) for information about dictionaries.

**`divmod(){:py}`.** You want `FLOOR.MATH{:gsheets}` for proper floor division:

```gsheets
DIVMOD(a, b)
= LET(q, FLOOR.MATH(a / b), {q, a - q*b})
```

**`enumerate(){:py}`.** Assuming you have a column vector, this returns an array, which you can treat as a list of its rows:

```gsheets
ENUMERATE(vector)
= HSTACK(SEQUENCE(ROWS(vector)), vector)
```

**`eval(){:py}`.** You probably want to use `INDIRECT{:gsheets}` instead.

**`filter(){:py}`.** The native equivalent `FILTER{:gsheets}` doesn't work with lambdas. Use:

```gsheets
FILTER_(vector, fn)
= FILTER(vector, MAP(vector, fn))
```

If you want to filter an array, treated as a list of its rows, use:

```gsheets
FILTER_(array, fn)
= FILTER(
  array,
  BYROW(array, LAMBDA(row, fn(TRANSPOSE(row))))
)
```

The `TRANSPOSE{:gsheets}` is needed to flip the rows to columns, so we can treat them in the same way our other built-in equivalents do.

**`float(){:py}`.** Use `TO_PURE_NUMBER{:gsheets}` or `N{:gsheets}`. See [Numeric types](#numeric-types) for information about floats.

**`hex(){:py}`.** Use `DEC2HEX{:gsheets}`.

**`int(){:py}`.** Use `INT{:gsheets}`, or `DECIMAL{:gsheets}` if you need to convert from a base. See [Numeric types](#numeric-types) for information about integers.

**`len(){:py}`.** Use `ROWS{:gsheets}` or `COLUMNS{:gsheets}`, depending on the shape. If you don't know, you can `FLATTEN{:gsheets}`.

**`list(){:py}`.** See [Lists and tuples](#lists-and-tuples) for information about lists.

**`map(){:py}`.** For vectors, use `MAP{:gsheets}`. To treat an array as a list of its rows, use:

```gsheets
MAP_(array, fn)
= BYROW(array, LAMBDA(row, fn(TRANSPOSE(row))))
```

The `TRANSPOSE{:gsheets}` is needed to flip the rows to columns, so we can treat them in the same way our other built-in equivalents do.

**`max(){:py}`.** Use `MAX{:gsheets}`. You can use a key function, on an array treated as a list of its rows, with:

```gsheets
MAX_(array, key)
= LET(
  mapped,
    BYROW(array, LAMBDA(row, key(TRANSPOSE(row)))),
  i,
    MATCH(MAX(mapped), mapped, 0),
  INDEX(array, i)
)
```

The `TRANSPOSE{:gsheets}` is needed to flip the rows to columns, so we can treat them in the same way our other built-in equivalents do.

**`min(){:py}`.** Use `MIN{:gsheets}`. You can use a key function, on an array treated as a list of its rows, with:

```gsheets
MIN_(array, key)
= LET(
  mapped,
    BYROW(array, LAMBDA(row, key(TRANSPOSE(row)))),
  i,
    MATCH(MIN(mapped), mapped, 0),
  INDEX(array, i)
)
```

The `TRANSPOSE{:gsheets}` is needed to flip the rows to columns, so we can treat them in the same way our other built-in equivalents do.

**`oct(){:py}`.** Use `DEC2OCT{:gsheets}`.

**`ord(){:py}`.** Use `CODE{:gsheets}`.

**`pow(){:py}`.** Use `POW{:gsheets}`. For the modulo, use, as a named function:

```gsheets
POW_MOD(base_, exp_, modulo)
= IF(exp_ = 0, 1,
  LET(
    half,
      POW_MOD(base_, FLOOR(exp_ / 2), modulo),
    double,
      MOD(half * half, modulo),
    IF(ISEVEN(exp_), double,
      MOD(double * base_, modulo)
    )
  )
)
```

It needs to be a named function for the recursion to work.

**`range(){:py}`.** You might not need this: if you're going to map immediately afterward, you can use `MAKEARRAY{:gsheets}`. The native equivalent is `SEQUENCE{:gsheets}`. If you want Python behavior and zero-indexing:

```gsheets
RANGE_(start_or_stop, stop, step)
= LET(
  start, IF(ISBLANK(IFERROR(stop)), 0, start_or_stop),
  stop_, IF(ISBLANK(IFERROR(stop)), start_or_stop, stop),
  step_, IF(ISBLANK(IFERROR(step)), 1, step),
  len, FLOOR.MATH((stop_ - start) / step_),
  IF(len <= 0, {},
    SEQUENCE(len, 1, start, step_)
  )
)
```

**`reversed(){:py}`.** To reverse the rows of an array:

```gsheets
REVERSED(array)
= SORT(array, SEQUENCE(ROWS(array)), FALSE)
```

**`round(){:py}`.** Use `ROUND{:gsheets}`.

**`set(){:py}`.** To deduplicate a vector, use `UNIQUE{:gsheets}`. See [Sets](#sets) for information about sets.

<a name="slice"></a>

**`slice(){:py}`.** To slice the rows of an array, with a given start and stop that are zero-indexed, use:

```gsheets
SLICE(array, start, stop, step)
= LET(
  step_, IF(ISBLANK(IFERROR(step)), 1, step),
  coerce, LAMBDA(idx, default,
    IFS(
      ISBLANK(IFERROR(idx)), default,
      idx < 0, ROWS(array) + idx,
      AND(step_ > 0, idx >= ROWS(array)), ROWS(array),
      AND(step_ < 0, idx >= ROWS(array) - 1), ROWS(array) - 1,
      TRUE, idx
    )
  ),
  start_, coerce(start, IF(step_ > 0, 0, ROWS(array) - 1)),
  stop_, coerce(stop, IF(step_ > 0, ROWS(array), -1)),
  len, FLOOR.MATH((stop_ - start_) / step_),
  IF(len <= 0, {},
    MAKEARRAY(len, COLUMNS(array), LAMBDA(i, j,
      INDEX(array, start_ + 1 + (i - 1) * step_, j)
    ))
  )
)
```

To slice columns, use with `TRANSPOSE{:gsheets}`. Writing a performant version for slicing rows and columns simultaneously is an exercise left to the reader.

**`sorted(){:py}`.** See [`list.sort(){:py}`.](#list-sort)

**`str(){:py}`.** Use `TO_TEXT{:gsheets}` or `T{:gsheets}`. See [Strings](#strings) for information about strings.

**`sum(){:py}`.** Use `SUM{:gsheets}` for numbers and `CONCATENATE{:gsheets}` for strings.

**`tuple(){:py}`.** See [Lists and tuples](#lists-and-tuples) for information about tuples.

**`type(){:py}`.** Use `TYPE{:gsheets}`, which returns a number that you have to convert. If you want Python-style names:

```gsheets
TYPE_(val)
= SWITCH(TYPE(val),
  1, IF(val = INT(val), "int", "float"),
  2, "str",
  4, "bool",
  16, "Exception",
  64, "list",
)
```

**`zip(){:py}`.** To zip two column vectors to an array treated as a list of its rows, use `HSTACK{:gsheets}`. It has exact behavior by default, placing error cells if the vectors are of different lengths.

To get shortest behavior, use:

```gsheets
ZIP(a, b)
= ARRAY_CONSTRAIN(HSTACK(a, b), MIN(ROWS(a), ROWS(b)), 2)
```

To get longest behavior, use `IFERROR(HSTACK(a, b)){:gsheets}`.

## Built-in types

### Boolean operators

The built-ins `AND{:gsheets}` and `OR{:gsheets}` do not short-circuit, but `IF{:gsheets}` does.

To get the Python behavior of `AND{:gsheets}` and `OR{:gsheets}`, where we return one of the arguments, short-circuit as needed, and do Python-style coercion to booleans, use:

```gsheets
AND_(x, y)
= LET(
  x_bool, SWITCH(TYPE(x),
    1, x <> 0,
    2, x <> "",
    4, x,
    64, AND(ROWS(x) > 0, COLUMNS(x) > 0),
    TRUE
  ),
  IF(x_bool, x, y)
)
```

and

```gsheets
OR_(x, y)
= LET(
  x_bool, SWITCH(TYPE(x),
    1, x <> 0,
    2, x <> "",
    4, x,
    64, AND(ROWS(x) > 0, COLUMNS(x) > 0),
    TRUE
  ),
  IF(x_bool, y, x)
)
```

### Comparisons

The comparison operators `<{:gsheets}`, `<={:gsheets}`, `>{:gsheets}`, `>={:gsheets}` carry over.

**`x == y{:py}`.** Use `x = y{:gsheets}`.

**`x != y{:py}`.** Use `x <> y{:gsheets}`.

### Numeric types

The arithmetic operators `+{:gsheets}`, `-{:gsheets}`, `*{:gsheets}`, `/{:gsheets}` carry over.

**`x // y{:py}`.** Use `FLOOR.MATH(x / y){:gsheets}`.

**`x % y{:py}`.** Use `MOD{:gsheets}`, or `x - FLOOR.MATH(x / y) * y{:gsheets}` if you want more similar Python behavior. I don't actually know what the difference is.

**`c.conjugate(){:py}`.** Use `IMCONJUGATE{:gsheets}`.

**`x ** y{:py}`.** Use `POW{:gsheets}`.

**`x | y{:py}`.** Use `BITOR{:gsheets}`.

**`x ^ y{:py}`.** Use `BITXOR{:gsheets}`.

**`x & y{:py}`.** Use `BITAND{:gsheets}`.

**`x << n{:py}`.** Use `BITLSHIFT{:gsheets}`.

**`x >> n{:py}`.** Use `BITRSHIFT{:gsheets}`.

**`~x{:py}`.** Use `-x - 1{:gsheets}`.

**`int.bit_length().`** Use `LEN(DEC2BIN(int)){:gsheets}`.

**`int.bit_count().`** Use `LEN(REGEXREPLACE(DEC2BIN(int), "0", "")){:gsheets}`.

**`float.is_integer(){:py}`.** Use `INT(float) = float{:gsheets}`.

### Lists and tuples

In general you can treat a single-column array as a list or a tuple. (Inputs are always immutable, so they're kinda the same.)

**`val in list{:py}`.** Use `NOT(ISNA(MATCH(val, list, 0))){:gsheets}`.

**`list + other{:py}`.** Use `VSTACK(list, other){:gsheets}`.

**`list * n{:py}`.** Use:

```gsheets
LIST_TIMES(list, n)
= REDUCE(
  {},
  SEQUENCE(n),
  LAMBDA(acc, _, VSTACK(acc, list))
)
```

**`list[i]{:py}`.** Use `INDEX(list, i){:gsheets}` if the index is guaranteed positive. Otherwise, see [`slice(){:py}`.](#slice)

**`list[i:j]`, `list[i:j:k]`.** See [`slice(){:py}`.](#slice)

**`list.index(val){:py}`.** Use `MATCH(val, list, 0){:gsheets}`.

**`list.count(val){:py}`.** Use `COUNTIF(list, val){:gsheets}`.

<a name="list-sort"></a>

**`list.sort(){:py}`.** Use `SORT(list){:gsheets}`. You can use a key function, on an array treated as a list of its rows, with:

```gsheets
LIST_SORT(array, key)
= SORT(array, MAP(array, LAMBDA(row, key(TRANSPOSE(row)))))
```

The `TRANSPOSE{:gsheets}` is needed to flip the rows to columns, so we can treat them in the same way our other built-in equivalents do.

### Strings

Strings have *so* many built-in methods that I'm not going to bother. Just check the [Google Sheets function list.](https://support.google.com/docs/table/25273?hl=en#query=text) Here's the common sequence methods though:

**`sub in str{:py}`.** Use `IFERROR(FIND(sub, str) <> 0, FALSE){:gsheets}`.

**`str + other{:py}`.** Use `str & other{:gsheets}`.

**`str * n{:py}`.** Use `REPT(str, n){:gsheets}`.

**`str[i]{:py}`.** Use `MID(list, i, 1){:gsheets}` if the index is positive. Otherwise, see [`str[i:j:k]{:py}`.](#str-i-j-k)

**`str[i:j]{:py}`.** Use `MID(list, i, j - i){:gsheets}` if indices are positive. Otherwise, see [`str[i:j:k]{:py}`.](#str-i-j-k)

<a name="str-i-j-k"></a>

**`str[i:j:k]{:py}`.** With zero-indexing:

```gsheets
STR_SLICE(str, start, stop, step)
= LET(
  step_, IF(ISBLANK(IFERROR(step)), 1, step),
  coerce, LAMBDA(index, default,
    IFS(
      ISBLANK(IFERROR(index)), default,
      index < 0, LEN(str) + index,
      AND(step_ > 0, index >= LEN(str)), LEN(str),
      AND(step_ < 0, index >= LEN(str) - 1), LEN(str) - 1,
      TRUE, index
    )
  ),
  start_, coerce(start, IF(step_ > 0, 0, LEN(str) - 1)),
  stop_, coerce(stop, IF(step_ > 0, LEN(str), -1)),
  len, FLOOR.MATH((stop_ - start_) / step_),
  IF(len <= 0, "", CONCATENATE(
    MAKEARRAY(len, 1, LAMBDA(i, _,
      MID(str, start_ + 1 + (i - 1) * step_, 1)
    ))
  ))
)
```

**`str.index(sub){:py}`.** Use `FIND(sub, str){:gsheets}`.

**`str.count(sub){:py}`.** With zero-indexing:

```gsheets
STR_COUNT(str, sub, start, end)
= LET(
  start_, IF(ISBLANK(IFERROR(start)), 0, start),
  end_, IF(ISBLANK(IFERROR(end)), LEN(str), end),
  IF(end_ <= start_, 0,
  LET(
    sub_start, IFERROR(FIND(sub, str, start_ + 1) - 1, -1),
    IF(sub_start = -1, 0, LET(
      sub_end, sub_start + LEN(sub),
      1 + STR_COUNT(str, sub, sub_end, end_)
    ))
  ))
)
```

### Sets

In general you can treat a single-column array without repeated elements as an (immutable) set.

**`val in set{:py}`.** Use `NOT(ISNA(MATCH(val, set, 0))){:gsheets}`.

**`set.isdisjoint(other){:py}`.** Use:

```gsheets
SET_ISDISJOINT(set, other)
= ISNA(FILTER(set, MATCH(set, other, 0)))
```

**`set <= other{:py}`.** Use:

```gsheets
SET_ISSUBSET(set, other)
= IF(
  ROWS(set) <= ROWS(other),
  IFNA(
    ROWS(FILTER(set, MATCH(set, other, 0))) = ROWS(set),
    FALSE
  ),
  FALSE
)
```

For `set < other{:py}`, use the above with `ROWS(set) < ROWS(other){:gsheets}`.

**`set | other{:py}`.** Use `UNIQUE(VSTACK(set, other)){:gsheets}`.

**`set & other{:py}`.** Use:

```gsheets
SET_AND(set, other)
= FILTER(set, MATCH(set, other, 0))
```

**`set - other{:py}`.** Use:

```gsheets
SET_MINUS(set, other)
= FILTER(set, ISNA(MATCH(set, other, 0)))
```

**`set ^ other{:py}`.** Use:

```gsheets
SET_SYMMETRIC_DIFFERENCE(set, other)
= VSTACK(
  FILTER(set, ISNA(MATCH(set, other, 0))),
  FILTER(other, ISNA(MATCH(other, set, 0)))
)
```

### Dictionaries

In general you can treat an array with key-value rows, and unique keys, as a dictionary.

**`dict[key]{:py}`.** Use `VLOOKUP(key, dict, 2, FALSE){:gsheets}`.

**`key in dict{:py}`.** Use:

```gsheets
IN_DICT(key, dict)
= NOT(ISNA(VLOOKUP(key, dict, 2, FALSE)))
```

**`dict.get(key, default){:py}`.** Use:

```gsheets
DICT_GET(dict, key, default)
= IFNA(VLOOKUP(key, dict, 2, FALSE), default)
```

**`dict.keys(){:py}`.** Use `INDEX(dict, 0, 1){:gsheets}`.

**`dict.values(){:py}`.** Use `INDEX(dict, 0, 2){:gsheets}`.

**`dict | other{:py}`.** Use:

```gsheets
DICT_OR(dict, other)
= VSTACK(other,
  FILTER(dict,
    BYROW(dict, LAMBDA(row,
      ISNA(VLOOKUP(INDEX(row, 0, 1), other, 2, FALSE))
    ))
  )
)
```
